import React, { useEffect, useId, useState } from "react";
import classNames from "classnames";
import styles from "./PaginationFooter.module.scss";

type PaginationFooterProps = {
  totalLabel: string;
  pageLabel: string;
  pageSizeLabel: string;
  total: number;
  page: number;
  pageCount: number;
  pageSize: number;
  previousLabel: string;
  nextLabel: string;
  goToLabel: string;
  goButtonLabel: string;
  invalidMessage: (max: number) => string;
  onPageChange: (page: number) => void;
  className?: string;
};

function PaginationFooter({
  totalLabel,
  pageLabel,
  pageSizeLabel,
  total,
  page,
  pageCount,
  pageSize,
  previousLabel,
  nextLabel,
  goToLabel,
  goButtonLabel,
  invalidMessage,
  onPageChange,
  className,
}: PaginationFooterProps) {
  const safePageCount = Math.max(pageCount, 1);
  const currentPage = Math.min(Math.max(page, 1), safePageCount);
  const inputId = useId();
  const [inputValue, setInputValue] = useState(String(currentPage));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInputValue(String(currentPage));
    setError(null);
  }, [currentPage]);

  const disablePrevious = currentPage <= 1;
  const disableNext = currentPage >= safePageCount;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const desired = Number(inputValue);
    if (!Number.isFinite(desired) || desired < 1 || desired > safePageCount) {
      setError(invalidMessage(safePageCount));
      return;
    }
    setError(null);
    onPageChange(desired);
  };

  const handlePrevious = () => {
    if (!disablePrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (!disableNext) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className={classNames(styles.footer, className)}>
      <div className={styles.metaStats}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{totalLabel}</span>
          <span className={styles.metaValue}>{total.toLocaleString()}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{pageLabel}</span>
          <span className={styles.metaValue}>
            {currentPage} / {safePageCount}
          </span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>{pageSizeLabel}</span>
          <span className={styles.metaValue}>{pageSize}</span>
        </div>
      </div>

      <div className={styles.controls}>
        <form className={styles.pageJumpForm} onSubmit={handleSubmit}>
          <label className={styles.pageJumpLabel} htmlFor={inputId}>
            {goToLabel}
          </label>
          <input
            id={inputId}
            className={styles.pageInput}
            type="number"
            min="1"
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              setError(null);
            }}
          />
          <button type="submit" className={styles.pageJumpButton}>
            {goButtonLabel}
          </button>
          {error && <span className={styles.pageError}>{error}</span>}
        </form>

        <div className={styles.navButtons}>
          <button
            type="button"
            className={styles.navButton}
            onClick={handlePrevious}
            disabled={disablePrevious}
          >
            {previousLabel}
          </button>
          <button
            type="button"
            className={styles.navButton}
            onClick={handleNext}
            disabled={disableNext}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaginationFooter;
